import { useCallback, useState, type JSX } from 'react';

import { type CalendarDate } from '@internationalized/date';
import { DatePicker, EloIcon, EloInputFieldLabel, EloInputHelperText, Field, Portal, chakra } from '@plurall/elo-v3';

import {
  timeInputEndContainerRecipe,
  timeInputFieldRecipe,
  timeInputTriggerRecipe,
  timeInputWrapperRecipe,
} from '@/components/TimeInput/TimeInput.recipe';

import { calendarDateToIso, isoToCalendarDate } from './dateTransformers';

import type { DateValue } from '@plurall/elo-v3';

const DatePickerControl = chakra(DatePicker.Control, timeInputWrapperRecipe);
const DatePickerInput = chakra(DatePicker.Input, timeInputFieldRecipe);
const DatePickerIndicatorGroup = chakra(DatePicker.IndicatorGroup, timeInputEndContainerRecipe);
const DatePickerTrigger = chakra(DatePicker.Trigger, timeInputTriggerRecipe);

type Props = {
  value: string | null;
  onChange: (iso: string | null) => void;
  label?: string;
  helperText?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  minDate?: string | null;
};

export function DateInput({
  value,
  onChange,
  label,
  helperText,
  error,
  placeholder = 'Selecione a data',
  disabled,
  minDate,
}: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const calDate = isoToCalendarDate(value);
  const minCalDate = isoToCalendarDate(minDate ?? null);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown' && !isOpen) {
        event.preventDefault();
        setIsOpen(true);
      }
    },
    [isOpen]
  );

  return (
    <Field.Root
      disabled={disabled}
      invalid={!!error}
      display='flex'
      flexDirection='column'
      gap='gap.3xsmall'>
      {label && <EloInputFieldLabel disabled={disabled}>{label}</EloInputFieldLabel>}

      <DatePicker.Root
        locale='pt-BR'
        openOnClick
        open={isOpen}
        onOpenChange={(details) => setIsOpen(details.open)}
        disabled={disabled}
        value={calDate ? ([calDate] as unknown as DateValue[]) : []}
        min={minCalDate ? (minCalDate as unknown as DateValue) : undefined}
        // DatePicker.Root expects DateValue[] (Elo's type), but CalendarDate comes from
        // @internationalized/date with no shared type ancestry. The two are structurally
        // compatible at runtime — the double cast bridges the gap without lying about the value.
        onValueChange={(details) => {
          const picked = details.value[0] as CalendarDate | undefined;
          onChange(calendarDateToIso(picked ?? null));
          setIsOpen(false);
        }}>
        <DatePickerControl>
          <DatePickerInput
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
          />
          <DatePickerIndicatorGroup>
            <DatePickerTrigger type='button'>
              <EloIcon
                icon='calendar'
                size='small'
              />
            </DatePickerTrigger>
          </DatePickerIndicatorGroup>
        </DatePickerControl>

        <Portal>
          <DatePicker.Positioner>
            <DatePicker.Content>
              <DatePicker.View view='day'>
                <DatePicker.Header />
                <DatePicker.DayTable />
              </DatePicker.View>
              <DatePicker.View view='month'>
                <DatePicker.Header />
                <DatePicker.MonthTable />
              </DatePicker.View>
              <DatePicker.View view='year'>
                <DatePicker.Header />
                <DatePicker.YearTable />
              </DatePicker.View>
            </DatePicker.Content>
          </DatePicker.Positioner>
        </Portal>
      </DatePicker.Root>

      {error ? (
        <EloInputHelperText
          text={error}
          variant='error'
        />
      ) : helperText ? (
        <EloInputHelperText text={helperText} />
      ) : null}
    </Field.Root>
  );
}
